import { CdkTrapFocus } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgOptimizedImage } from '@angular/common';
import { Component, computed, ElementRef, inject, linkedSignal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map } from 'rxjs/operators';

import { CustomerSessionService } from '../auth/customer-session.service';
import { EMPIRIA_CONTACT } from '../contact/empiria-contact';
import { APP_PATHS } from '../routing/app-paths';

/**
 * PRÓG SZUFLADY, BLIŹNIACZY Z ARKUSZEM — `$md: 768px` w `src/styles/_breakpoints.scss`.
 *
 * Do S-22 komponent progu NIE ZNAŁ i było to świadome („próg zna arkusz, a komponent zna
 * wyłącznie intencję użytkownika"). Pułapka fokusu tę równowagę kończy: pułapka uzbrojona
 * na szerokim ekranie, gdzie nawigacja stoi rozwinięta w pasku, zamknęłaby klawiaturę
 * w pasku nawigacji na KAŻDEJ trasie publicznej — czyli lekarstwo gorsze od choroby
 * z C-1. Żeby uzbroić ją wyłącznie tam, gdzie szuflada naprawdę jest nakładką, warunek
 * musi być czytelny w TypeScripcie.
 *
 * `767.98px` zamiast `767px`: media query z ułamkiem domyka przedział tuż pod progiem
 * arkusza (ta sama konwencja, co w `@angular/cdk/layout`), więc przy szerokości dokładnie
 * 768 px arkusz rozwija nawigację i pułapka jest już rozbrojona — nie ma jednego piksela,
 * w którym obie decyzje mówią co innego.
 */
const DRAWER_BREAKPOINT = '(max-width: 767.98px)';

/**
 * RESIDUAL shell of the public part of the panel: skip-link, top bar, routed content
 * and a copyright-only footer. Since P2 („Poprawki do panelu klienta") the public
 * area is gone — home, catalog, calendar and contact left this layout (the last
 * three live on as panel sections) — so the only routes rendered here are `/zapis`
 * (public by Filip's decision: mailing deep links must work without a session)
 * and the 404 screen.
 *
 * The nav still reads the session, and that is ALL it does now: it labels its only
 * entry as the auth pair versus the panel shortcut. The drawer machinery (focus
 * trap, scrim, Escape) stays — two links on a phone are still an overlay.
 */
@Component({
  selector: 'app-public-layout',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatButtonModule,
    MatToolbarModule,
    NgOptimizedImage,
    CdkTrapFocus,
  ],
  // ESCAPE ZAMYKA SZUFLADĘ Z DOWOLNEGO MIEJSCA POWŁOKI, a nie tylko wtedy, gdy fokus stoi
  // w samej szufladzie: nasłuch na hoście łapie zdarzenie także w tej chwili po otwarciu,
  // zanim pułapka przeniesie fokus. Strażnik „menu jest otwarte" siedzi w `dismissMenu`,
  // więc Escape na trasie bez rozwiniętego menu nie porywa fokusu na przełącznik.
  host: { '(keydown.escape)': 'dismissMenu()' },
  template: `
    <!-- The skip-link's look lives in src/styles/_a11y.scss, shared with the panel
         layout through the a11y.skip-link mixin below. Only the behaviour stays here:
         href="#main-content" would resolve against <base href="/"> and leave the current
         route, so the handler moves focus itself; the href stays for link semantics. -->
    <a class="skip-link" href="#main-content" (click)="skipToContent($event, main)">
      Przejdź do treści
    </a>
    <header>
      <!-- PRZYCIEMNIONE TŁO ZA SZUFLADĄ — ten sam traktat, co w powłoce panelu, gdzie
           robi to nakładka MatSidenava. Makieta 'katalog_szkole_mobile_lista_menu_edupanel'
           przyciemnia treść pod szufladą (rozmycia z tamtej makiety NIE bierzemy: właściciel
           odrzucił dekoracje 'backdrop-blur', verdicts/README.md §2.3).

           PRZYCISKIEM, nie dywanikiem z '(click)': kliknięcie w tło jest tu prawdziwą
           akcją („zamknij menu"), a akcja powieszona na '<div>' nie ma nazwy, nie łapie
           klawiatury i wywala regułę a11y lintera. Przycisk jest pusty wzrokowo, więc
           nazwę niesie 'aria-label'.

           STOI PRZED PASKIEM w drzewie i to jest cała mechanika warstw: '<header>' ma
           własny kontekst nakładania (warstwa nr 1), a wewnątrz niego późniejsze rodzeństwo
           maluje się WYŻEJ — więc pasek i rozwinięta szuflada zostają nad przyciemnieniem,
           a treść strony pod nim. Żadna nowa deklaracja 'z-index' nie jest do tego
           potrzebna, co jest istotne: krok 2 verify:s22-p2 enumeruje wszystkie warstwy
           i żadna nie ma prawa przebić skip-linku. -->
      @if (menuOpen()) {
        <button
          type="button"
          class="nav-scrim"
          aria-label="Zamknij menu nawigacji"
          (click)="dismissMenu()"
        ></button>
      }
      <!-- PUŁAPKA FOKUSU STOI NA PASKU, A NIE NA SAMEJ SZUFLADZIE, i to jest cała różnica
           między pułapką działającą a pułapką bez wyjścia: krzyżyk zamykający szufladę
           JEST przełącznikiem w pasku (rozwinięte menu zamienia mu ścieżkę na krzyżyk),
           czyli leży POZA elementem nawigacji. Pułapka założona na samej nawigacji
           odcięłaby klawiaturę od jedynego WIDOCZNEGO wyjścia — zostałby sam Escape,
           którego trzeba się domyślić. Pasek obejmuje krzyżyk i szufladę, a NIE obejmuje
           przyciemnienia (to rodzeństwo paska) ani treści strony, więc krąg tabulacji to
           dokładnie „zamknij + pozycje menu".

           MECHANIZM JEST TEN SAM, KTÓRY DOSTAJE POWŁOKA PANELU — FocusTrap z CDK.
           MatDrawer w trybie nakładki robi dokładnie to: tworzy pułapkę przez
           FocusTrapFactory, włącza ją, gdy szuflada jest otwarta i widać przyciemnienie,
           zabiera fokus przy otwarciu i oddaje przy zamknięciu, a Escape obsługuje własnym
           nasłuchem (node_modules/@angular/material/fesm2022/sidenav.mjs, sprawdzone
           2026-08-17). Powłoka publiczna nie może wziąć całego MatSidenava: jej nawigacja
           powyżej progu tabletu jest rzędem w pasku, a nie szufladą, więc kontener
           sidenava musiałby objąć całą stronę razem ze stopką i utrzymywać drugie,
           bliźniacze drzewo nawigacji na szeroki ekran. Zamiast przepisywać powłokę pod
           cudzy komponent, bierzemy z niego to, czego brakowało — tę samą pułapkę z CDK,
           to samo wyjście Escape, ten sam powrót fokusu. Bez sięgania do cudzego wnętrza
           i bez własnej implementacji krążenia po tabulacji.

           BEZ ANIMACJI, więc preferencja ograniczonego ruchu nie ma tu czego uszanować:
           szuflada pojawia się i znika razem z klasą is-open, a jedyne przejścia w tym
           arkuszu (barwy pozycji menu) zeruje globalny blok tej preferencji w
           src/styles.scss. -->
      <mat-toolbar [cdkTrapFocus]="menuOpen()" [cdkTrapFocusAutoCapture]="menuOpen()">
        <!-- ZNAK FIRMOWY Z PANELU ADMINA, nie zastępczy rysunek: 'public/logo.png' to
             kopia 'front/src/assets/images/logo.png' (190×49, RGBA), czyli DOKŁADNIE ten
             sam plik, który widzi biuro w swoim panelu. Marka przestała być składana
             z zastępczego znaku i napisu, bo składanie jej z części znaczyło rysowanie
             logo, którego nikt nie zatwierdzał.

             'NgOptimizedImage' (wymóg .claude/CLAUDE.md dla statyk) potrzebuje wymiarów
             WŁASNYCH pliku — 190×49 — a nie tych, w jakich obraz stoi na ekranie: z nich
             liczy proporcję i rezerwuje miejsce, więc pasek nie skacze przy dojściu
             obrazu. Wysokość na ekranie ustawia arkusz, ze skali.

             BEZ 'priority' ŚWIADOMIE: atrybut jest deklaracją „to jest element LCP", a
             logo o wysokości 32 px nim nie jest na żadnym ekranie tego panelu — Angular
             wypisałby wtedy NG02955 w konsoli DEV przy każdym wejściu. Obraz stoi w
             pierwszym widoku, więc przeglądarka pobiera go natychmiast mimo
             'loading="lazy"' (odroczenie dotyczy wyłącznie obrazów poza widokiem).

             TEKST ALTERNATYWNY NIESIE TO, CO WIDAĆ NA ZNAKU („SALON EDUKACYJNY” +
             „empiria”), a nie pełną nazwę spółki ze stopki — czytnik ekranu ma dostać
             to samo, co oko, a nie wypis z KRS. -->
        <span class="brand">
          <img ngSrc="/logo.png" width="190" height="49" alt="Salon Edukacyjny EMPIRIA" />
        </span>
        <!-- MENU MOBILNE. Pozycje są w drzewie ZAWSZE, a chowa je wyłącznie
             'display: none' spod progu tabletu — dzięki temu ukryte menu znika też z
             drzewa dostępności i z tabulacji, czego 'visibility'/przezroczystość nie
             załatwiają. Powyżej progu nawigacja stoi rozwinięta, a ten przycisk znika,
             więc 'menuOpen' jest tam z definicji fałszywe. -->
        <button
          #navToggle
          mat-icon-button
          class="icon-button nav-toggle"
          type="button"
          aria-label="Menu nawigacji"
          aria-controls="public-nav"
          [attr.aria-expanded]="menuOpen()"
          (click)="toggleMenu()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
               stroke-linecap="round" aria-hidden="true">
            @if (menuOpen()) {
              <path d="m6 6 12 12M18 6 6 18" />
            } @else {
              <path d="M4 7h16M4 12h16M4 17h16" />
            }
          </svg>
        </button>
        <!-- Menu części publicznej (Strona główna / Katalog / Kalendarz / Kontakt)
             zniknęło razem z częścią publiczną (P2) — te ekrany żyją teraz w panelu.
             Zostaje wyłącznie rozgałęzienie sesji: para auth dla anonima na /zapis,
             skrót do panelu dla zalogowanego. -->
        <nav id="public-nav" aria-label="Nawigacja główna" [class.is-open]="menuOpen()">
          @if (isAuthenticated()) {
            <a
              [routerLink]="['/', panelPath]"
              routerLinkActive="is-active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu(main)"
            >
              Panel
            </a>
          } @else {
            <a
              class="cta cta-secondary"
              [routerLink]="['/', loginPath]"
              routerLinkActive="is-active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu(main)"
            >
              Zaloguj się
            </a>
            <a
              class="cta cta-primary"
              [routerLink]="['/', registerPath]"
              routerLinkActive="is-active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu(main)"
            >
              Zarejestruj się
            </a>
          }
        </nav>
      </mat-toolbar>
    </header>
    <main id="main-content" tabindex="-1" #main>
      <router-outlet />
    </main>
    <!-- STOPKA TO SAM RZĄD PRAW AUTORSKICH (P3, „Poprawki do panelu klienta"): strefa
         kolumn (logo, Kontakt, Godziny pracy, Serwis) zniknęła razem z częścią
         publiczną — jej treść linkowała ekrany, które przeniosły się za logowanie.
         Pasek zostaje dla spójności z panelem, bo ten layout wciąż renderuje /zapis
         i 404. -->
    <footer>
      <p class="footer-copyright">© {{ currentYear }} {{ contact.companyName }}</p>
    </footer>
  `,
  styles: `
    @use 'a11y';
    @use 'breakpoints' as bp;

    /* ŻADNEJ SUROWEJ DŁUGOŚCI W TYM BLOKU — pilnuje tego krok 1 bramki
       'npm run verify:s22-p2'. Miary czyta się z warstwy tokenów fazy 1; ta faza nie
       deklaruje ani jednego tokenu. Dlatego zniknął też trzypikselowy pasek marki spod
       paska nawigacji: 3px nie było żadnym stopniem skali, a rolę „marka na ekranie"
       przejął znak graficzny malowany tym samym tokenem. */

    :host {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
    }

    @include a11y.skip-link;

    /* KONSUMENT TOKENÓW MARKI W src/app (poza nim zieleń Empirii istnieje wyłącznie jako
       deklaracja w src/styles/_brand.scss, czyli marka nie weszłaby na ekran — pilnuje
       tego krok 3 verify:s22-p1, PER TOKEN i z wymogiem konsumenta w core/layout/**).
       Dwa tokeny, dwa różne progi WCAG i to NIE jest dublowanie:
       · --empiria-brand-text (5,29:1 na bieli, 5,05:1 na surface) niesie TEKST — tutaj
         sygnaturę pod znakiem w stopce, bo tylko ten wariant przechodzi próg 4,5:1;
       · --empiria-brand-accent (3,85:1 na bieli) maluje POWIERZCHNIĘ nietekstową — tutaj
         kreskę zamykającą pasek nawigacji, próg 3:1 z WCAG 1.4.11. Pod tekstem nie wolno
         go postawić i nie stoi.
       Wartości obu odcieni stoją w src/styles/_brand.scss i nigdzie indziej: literał hex
       w ekranie odbiera zasięg podmianie palety, czego pilnuje krok 6 verify:s22-p1.

       ⚠️ TO ZDANIE ZMIENIŁO SIĘ RAZEM ZE ZNAKIEM. Do jednostki „logo + favicon" marką na
       ekranie był rysunek SVG malowany akcentem i napis „EMPIRIA" malowany tokenem
       tekstowym — czyli marka SKŁADANA z tokenów. Teraz znak jest PLIKIEM (public/logo.png
       z panelu admina), a plik rastrowy nie słucha żadnego tokenu: w trybie wysokiego
       kontrastu widgetu dostępności zostanie taki, jaki jest. (Skala szarości go NIE
       omija — tam działa globalny 'filter: grayscale(1)' na 'html', czyli filtr obrazu,
       a nie podmiana tokenów; sprawdzone w src/styles/_a11y-preferences.scss.) Dlatego
       tokeny marki nie znikają razem z SVG, tylko przenoszą się na elementy, które im
       podlegają — inaczej po włączeniu trybu wysokiego kontrastu marka zostałaby na
       ekranie WYŁĄCZNIE jako niezmienny obrazek. Sama zieleń pliku mierzy 3,94:1 na
       bieli (wzór WCAG), więc jako obiekt graficzny przechodzi próg 3:1. Wartości nie
       wpisano tu liczbą szesnastkową świadomie: krok 6 tej samej bramki liczy literały
       hex w src/app/** RAZEM z komentarzami. */
    header {
      position: sticky;
      top: 0;
      /* Nad przewijaną treścią, POD skip-linkiem (1001) — krok 2 bramki fazy rozstrzyga
         tokeny do liczb i wymaga, żeby żadna warstwa go nie przebiła. */
      z-index: 1;
    }

    /* Kreska pod paskiem jest ZIELENIĄ MARKI, a nie neutralnym obrysem: to ona niesie
       akcent na każdej trasie publicznej, odkąd znak jest rastrem. Grubość 1px stoi na
       allowliście kroku 1 verify:s22-p2 (kreska włosowa nie jest miarą niczego w skali),
       więc nie wraca tu żaden „pasek marki" o wysokości spoza skali. */
    /* 'position: relative' JEST TU MECHANIZMEM WARSTW, nie nawykiem. Przyciemnienie stoi
       'position: fixed', a element pozycjonowany maluje się NAD niepozycjonowanym
       rodzeństwem niezależnie od kolejności w drzewie — zmierzone 2026-08-17:
       'document.elementFromPoint' w pasku zwracał nakładkę, czyli marka i krzyżyk były
       przygaszone razem z treścią, choć mają zostać czytelne. Pasek pozycjonowany i
       stojący w drzewie PO nakładce wygrywa bez ani jednej deklaracji 'z-index' — a to
       jest istotne, bo krok 2 verify:s22-p2 enumeruje wszystkie warstwy i żadna nie ma
       prawa przebić skip-linku.
       Przy okazji pasek staje się blokiem zawierającym dla szuflady, więc jej
       'inset-block-start: 100%' odmierza się od jego dolnej krawędzi. */
    mat-toolbar {
      position: relative;
      flex-wrap: wrap;
      gap: var(--empiria-space-2xs);
      height: auto;
      padding-block: var(--empiria-space-2xs);
      border-bottom: 1px solid var(--empiria-brand-accent);
      background: var(--mat-sys-surface);
    }

    .brand {
      display: inline-flex;
      align-items: center;
      margin-right: auto;
    }

    /* Wysokość ze skali, szerokość z proporcji pliku — 'auto' jest na allowliście kroku 1
       verify:s22-p2, bo nie jest miarą, tylko rezygnacją z miary. Dzięki temu proporcja
       190×49 zostaje nietknięta i NgOptimizedImage nie melduje jej zniekształcenia. */
    .brand img {
      width: auto;
      height: var(--empiria-space-lg);
    }

    .icon-button svg {
      width: var(--empiria-space-md);
      height: var(--empiria-space-md);
    }

    /* Poniżej progu tabletu nawigacja jest SZUFLADĄ, a nie listą doklejoną pod paskiem —
       ten sam traktat, co szuflada panelu, bo obie makiety mobilne pokazują to samo:
       białą taflę przy lewej krawędzi, treść pod przyciemnieniem, wyjście krzyżykiem.
       'display: none' zabiera ją też z tabulacji, więc zwinięte menu nie łapie fokusu. */
    nav {
      display: none;
      flex-basis: 100%;
      flex-direction: column;
      gap: var(--empiria-space-2xs);
      padding-bottom: var(--empiria-space-xs);
    }

    /* SZUFLADA WISI POD PASKIEM, nie od górnej krawędzi ekranu, i to jest różnica wobec
       makiety podjęta świadomie: pasek tej powłoki niesie DOKŁADNIE to, co makieta rysuje
       w górnej belce szuflady — znak marki po lewej, krzyżyk po prawej (przełącznik
       zamienia hamburger na krzyżyk, gdy menu jest otwarte) i włosową kreskę pod spodem.
       Drugi taki pasek WEWNĄTRZ szuflady byłby tym samym znakiem dwa razy w jednym
       dokumencie, czyli szumem dla czytnika ekranu.

       Miary: '100%' odsuwa taflę o całą wysokość paska (jego wysokość jest 'auto', więc
       liczby tu nie ma i być nie może), a 'calc(100dvh - 100%)' domyka ją dokładnie na
       dolnej krawędzi okna — bez tego odjęcia tafla wystawałaby poniżej ekranu o
       wysokość paska i wydłużała dokument. Obie wartości stoją na allowliście kroku 1
       verify:s22-p2, bo żadna nie jest miarą wybraną na oko.

       Szerokość czyta '--empiria-sidenav-width' — ten sam token, co szuflada panelu:
       „ten sam traktat" znaczy tu także tę samą szerokość, a nie dwie bliskie sobie
       liczby w dwóch powłokach. */
    nav.is-open {
      position: absolute;
      inset-block-start: 100%;
      inset-inline-start: 0;
      display: flex;
      box-sizing: border-box;
      width: var(--empiria-sidenav-width);
      max-width: 100%;
      height: calc(100dvh - 100%);
      padding: var(--empiria-space-sm) var(--empiria-space-xs);
      overflow-y: auto;
      border-right: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface-container-lowest);
    }

    /* Przyciemnienie treści pod szufladą. Barwa jest MIESZANKĄ ROLI JUŻ ZMIERZONEJ
       ('on-surface'), a nie nową rolą koloru: rejestr par (scripts/design-system/
       token-pairs.mjs) pilnuje, żeby każda rola użyta w src/** miała wpis, a ta faza nie
       dokłada do rejestru ani jednego wiersza. Receptura jest ta sama, którą Material
       stosuje do własnej nakładki sidenava — ciemny odcień zmieszany z przezroczystością —
       więc obie szuflady przyciemniają porównywalnie mocno.

       UDZIAŁ TO POŁOWA, a nie odczytane z Materiala 40%: udział jest tu ułamkiem, nie
       miarą, i krok 1 verify:s22-p2 przepuszcza z tej rodziny wyłącznie '50%' i '100%'
       (allowlista wartości, które niczego nie mierzą). Napisanie '40%' byłoby literałem
       spoza skali w bloku, który skali podlega — a token krycia w tej warstwie nie
       istnieje i ta faza nie ma prawa go zadeklarować. */
    /* BEZ 'width'/'height': 'inset: 0' rozpina element na wszystkie cztery krawędzie, więc
       wymiary byłyby powtórzeniem tej samej decyzji — a budżet stylu komponentu (4 kB
       ostrzeżenia, angular.json) ta powłoka przekroczyła o 42 bajty, czyli dokładnie
       o takie powtórzenia. */
    .nav-scrim {
      position: fixed;
      inset: 0;
      padding: 0;
      border: 0;
      background: color-mix(in srgb, var(--mat-sys-on-surface) 50%, transparent);
    }

    nav a {
      padding: var(--empiria-space-xs) var(--empiria-space-sm);
      border-radius: var(--empiria-radius-pill);
      color: var(--mat-sys-on-surface-variant);
      font: var(--mat-sys-label-large);
      text-decoration: none;
      transition:
        background-color var(--empiria-motion-duration) ease-out,
        color var(--empiria-motion-duration) ease-out;
    }

    nav a:hover {
      background: var(--mat-sys-surface-container);
      color: var(--mat-sys-on-surface);
    }

    /* Aktywna pozycja rozpoznawalna BEZ koloru (WCAG 1.4.1): tło jest trzecim sygnałem,
       pierwsze dwa to grubszy krój i podkreślenie z odsunięciem. */
    nav a.is-active {
      background: var(--mat-sys-surface-container);
      color: var(--mat-sys-on-surface);
      font-weight: var(--empiria-font-weight-bold);
      text-decoration: underline;
      text-underline-offset: var(--empiria-space-2xs);
    }

    /* Para logowanie/rejestracja to WEZWANIA DO DZIAŁANIA, nie kolejne pozycje listy —
       stąd inny ciężar wizualny. Wypełniony wariant stoi na parze primary/on-primary,
       zmierzonej w rejestrze kontrastu; obrys drugiego czyta outline, nie zieleń marki. */
    nav a.cta {
      text-align: center;
    }

    nav a.cta-secondary {
      border: 1px solid var(--mat-sys-outline);
      color: var(--mat-sys-on-surface);
    }

    nav a.cta-primary {
      background: var(--mat-sys-primary);
      box-shadow: var(--empiria-elevation-1);
      color: var(--mat-sys-on-primary);
    }

    nav a.cta-primary:hover {
      background: var(--mat-sys-primary);
      color: var(--mat-sys-on-primary);
    }

    @include bp.min-md {
      mat-toolbar {
        flex-wrap: nowrap;
      }

      /* Przełącznik i przyciemnienie znikają RAZEM i z jednego powodu: powyżej progu
         nawigacja stoi rozwinięta w pasku, więc nie ma czego przełączać ani co
         przysłaniać. Jedna reguła zamiast dwóch bliźniaczych — ta powłoka jest 4 bajty
         pod budżetem stylu komponentu (4 kB), więc każde powtórzenie ma tu cenę. */
      .nav-toggle,
      .nav-scrim {
        display: none;
      }

      nav {
        display: flex;
        flex-basis: auto;
        flex-direction: row;
        align-items: center;
        padding-bottom: 0;
      }

      /* ROZBROJENIE SZUFLADY POWYŻEJ PROGU, a nie ozdoba: 'menuOpen' jest stanem
         KOMPONENTU i przeżywa zmianę szerokości okna. Ktoś, kto rozwinie menu na
         telefonie i obróci tablet, zostałby bez tych dwóch reguł z taflą i
         przyciemnieniem na ekranie, na którym nawigacja i tak stoi rozwinięta w pasku —
         a przełącznika zamykającego już tam nie ma, bo powyżej progu jest ukryty.
         Selektory muszą mieć co najmniej specyficzność 'nav.is-open', inaczej reguła
         szuflady wygrywa mimo późniejszej pozycji w arkuszu. */
      nav.is-open {
        position: static;
        width: auto;
        height: auto;
        padding: 0;
        overflow-y: visible;
        border-right: 0;
        background: none;
      }
    }

    /* 'isolation: isolate' ZAMYKA TREŚĆ W JEDNEJ WARSTWIE i naprawia zastany błąd, który
       widać dopiero pod szufladą (zmierzone 2026-08-17): etykieta KAŻDEGO przycisku
       Materiala to 'span.mdc-button__label', element pozycjonowany stojący na warstwie
       nr 1. Ta jedynka konkuruje wprost z warstwą nagłówka, bo dotąd nic nie zamykało
       treści we własnym kontekście nakładania — a przy remisie wygrywa element
       PÓŹNIEJSZY w drzewie, czyli
       treść. Skutkiem był napis „Wyczyść filtry" katalogu malowany NA białej tafli
       rozwiniętej szuflady i, od zawsze, na przyklejonym pasku przy przewijaniu.
       'isolation' zamiast 'z-index' świadomie: tworzy kontekst nakładania, nie dokładając
       ani jednej warstwy do zbioru, który enumeruje krok 2 verify:s22-p2 — i nie każe
       nikomu zgadywać, dlaczego akurat „2". Powłoka panelu tego nie potrzebuje: tam treść
       zamyka '.mat-drawer-content' Materiala, który ma własny kontekst. */
    main {
      flex: 1;
      isolation: isolate;
      padding: var(--empiria-space-md);
    }

    /* Stopka, a nie main — to ONA jest ostatnim blokiem tego layoutu, więc to na niej
       musi zostać rezerwa na przycisk widgetu dostępności stojący position: fixed
       w prawym dolnym rogu każdej strony (WCAG 2.2 SC 2.4.11, i SC 1.4.10 przy 400%
       powiększenia). Uzasadnienie wysokości i drugiej połowy rezerwy —
       scroll-padding-block-end na html — stoi w src/styles.scss. */
    /* SAM RZĄD PRAW AUTORSKICH (P3): strefa kolumn odeszła razem z częścią publiczną,
       więc kreskę oddzielającą niesie teraz sama stopka, nie wiersz copyright.
       Rezerwa 'padding-block-end' zostaje — stopka jest ostatnim blokiem strony,
       więc to na niej siedzi miejsce na przycisk widgetu dostępności (uzasadnienie
       w src/styles.scss). */
    footer {
      padding: var(--empiria-space-md);
      padding-block-end: calc(var(--empiria-space-3xl) + var(--empiria-space-sm));
      border-top: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface);
    }

    .footer-copyright {
      margin: 0;
      color: var(--mat-sys-on-surface-variant);
      font: var(--mat-sys-body-small);
    }
  `,
})
export class PublicLayoutComponent {
  /**
   * Read straight off the session signal — no refresh is triggered from here.
   * A public page must not fire an authentication call just to label a link, so
   * the status stays `unknown` on a cold start on public content and the nav
   * shows the auth pair. Inside a live SPA session (after login, or after the
   * panel guard settled the status) the shortcut appears. The links are pure
   * navigation: nothing here gates access, which the guard and the backend do.
   */
  protected readonly isAuthenticated = inject(CustomerSessionService).isAuthenticated;

  /**
   * Nazwa spółki do rzędu praw autorskich — z JEDYNEJ kanonicznej stałej (S-17),
   * nigdy przepisana. Po P3 stopka czyta z niej wyłącznie `companyName`.
   */
  protected readonly contact = EMPIRIA_CONTACT;

  /**
   * Rok w prawach autorskich liczony w RUNTIME, nie wpisany.
   *
   * `new Date()` nie jest tu tym samym ryzykiem, przed którym ostrzegają reguły projektu:
   * te dotyczą treści zależnej od „dziś" (terminy, kalendarz), której nie wolno zamrozić
   * prerenderem. Trasy publiczne stoją na `RenderMode.Client`, więc ta wartość powstaje
   * u klienta przy każdym wejściu — a rok wpisany na sztywno kłamałby od 1 stycznia.
   */
  protected readonly currentYear = new Date().getFullYear();

  protected readonly loginPath = APP_PATHS.login;
  protected readonly registerPath = APP_PATHS.register;
  protected readonly panelPath = APP_PATHS.panel;

  /**
   * Przełącznik menu — po nim wraca fokus po zamknięciu szuflady (WCAG 2.4.3). Czytany
   * jako `ElementRef`, bo `mat-icon-button` jest komponentem Materiala i bez `read`
   * `viewChild` oddałby jego instancję zamiast elementu DOM.
   */
  private readonly navToggle = viewChild.required<unknown, ElementRef<HTMLButtonElement>>(
    'navToggle',
    { read: ElementRef },
  );

  /**
   * Czy szuflada jest w ogóle NAKŁADKĄ — patrz `DRAWER_BREAKPOINT` nad klasą. Powyżej progu
   * nawigacja stoi rozwinięta w pasku i nie ma czego ani chwytać, ani zamykać.
   */
  private readonly isDrawer = toSignal(
    inject(BreakpointObserver)
      .observe(DRAWER_BREAKPOINT)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  /**
   * Intencja użytkownika. `linkedSignal` z `isDrawer` jako źródłem: przejście przez próg
   * (obrót tabletu, zmiana szerokości okna) ZERUJE stan, tak samo jak w powłoce panelu.
   * Bez tego menu rozwinięte na telefonie wracałoby rozwinięte po powrocie spod progu,
   * razem z uzbrojoną pułapką fokusu.
   */
  private readonly menuOpenState = linkedSignal<boolean, boolean>({
    source: this.isDrawer,
    computation: () => false,
  });

  /**
   * Stan, którym steruje SZABLON: nie sama intencja, tylko intencja ORAZ próg. Ten iloczyn
   * jest jedynym miejscem, w którym pułapka fokusu może zostać uzbrojona — a to znaczy, że
   * nie da się jej uzbroić na szerokim ekranie żadną kolejnością zdarzeń.
   */
  protected readonly menuOpen = computed(() => this.menuOpenState() && this.isDrawer());

  protected toggleMenu(): void {
    this.menuOpenState.update((open) => !open);
  }

  /**
   * WYJŚCIE Z NAKŁADKI: Escape, kliknięcie w przyciemnione tło i krzyżyk w pasku. Wszystkie
   * trzy kończą się TAK SAMO — menu zwinięte i fokus z powrotem na przycisku, który je
   * otworzył. To druga połowa pułapki fokusu: bez powrotu fokus zostałby na elemencie
   * zwiniętym do `display: none` i wypadł do `<body>`, czyli użytkownik klawiatury
   * straciłby miejsce w dokumencie (WCAG 2.4.3).
   *
   * Strażnik na wejściu jest wymagany: nasłuch Escape wisi na hoście powłoki, więc bez
   * niego ucieczka z dowolnego widżetu na trasie publicznej porywałaby fokus na przełącznik
   * menu, którego nikt nie otwierał. Czyta `menuOpenState`, a nie `menuOpen`, bo po
   * przejściu progu iloczyn jest już fałszywy, a stan wciąż wymaga wyzerowania.
   */
  protected dismissMenu(): void {
    if (!this.menuOpenState()) {
      return;
    }

    this.menuOpenState.set(false);

    if (this.isDrawer()) {
      this.navToggle().nativeElement.focus();
    }
  }

  /**
   * Wybór pozycji na wąskim ekranie zwija menu i przenosi fokus na treść — bo pozycja,
   * która właśnie zniknęła z układu (`display: none`), zabrałaby fokus do `<body>`, a to
   * jest zgubienie miejsca w dokumencie, nie drobiazg wizualny. Ten sam powód i ten sam
   * porządek co w layoucie panelu: najpierw zwinięcie, potem fokus.
   *
   * Strażnik na początku jest połową funkcji: na szerokim ekranie menu nigdy nie jest
   * otwarte, więc bez niego każde kliknięcie w nawigację odbierałoby fokus kotwicy,
   * której użytkownik właśnie użył.
   *
   * RÓŻNICA WOBEC `dismissMenu` jest celowa i wynika z tego, DOKĄD użytkownik zmierza:
   * wybór pozycji to podróż do treści, więc fokus ląduje na `<main>`; Escape, tło i
   * krzyżyk to rezygnacja, więc fokus wraca tam, skąd wyszedł — na przełącznik.
   */
  protected closeMenu(main: HTMLElement): void {
    if (!this.menuOpenState()) {
      return;
    }

    this.menuOpenState.set(false);
    main.focus();
  }

  protected skipToContent(event: Event, target: HTMLElement): void {
    event.preventDefault();
    target.focus();
  }
}
